/**
 * Логика генерации размерных линий для коллекторов
 * Взято из FloorCollectorEditor.js (строки 5063+)
 */

import { formatDimension } from './collectorUtils';

/**
 * Генерирует размерную линию для торцевого подключения
 * Логика взята из FloorCollectorEditor.js (строки 5320-5560)
 * @param {Object} params - параметры
 * @returns {Object|null} - объект размерной линии или null
 */
export const generateConnectionDimensionLine = (params) => {
  const {
    connectionInfo,
    mainPipeId,
    mainPipeDiameter,
    startY,
    direction, // 'left' или 'right'
    dimY, // уровень размерной линии
  } = params;

  if (!connectionInfo) return null;

  const {
    id,
    type,
    fittingKey,
    length,
    startX,
    intermediatePipeId,
    bortshaibaId,
    capId,
    capType, // 'straight' или 'elliptical'
    capLength, // Длина прямой заглушки
    needsCapOrReducer,
  } = connectionInfo;

  const fittingCenterY = startY + mainPipeDiameter / 2;

  // Проверяем тип фитинга
  const isFlange = fittingKey?.startsWith('Flanec_');
  const isFreeFlange = isFlange && !fittingKey?.endsWith('_welded');
  const isWeldedFlange = isFlange && !isFreeFlange;
  const isNakGaika = fittingKey?.startsWith('Nak_Gaika_');
  const isShaibaNarRezba = fittingKey?.startsWith('Shaib_Nar_Rezba_');
  const isPipe = type === 'cylinder';

  // Для свободных фланцев: размерная линия ТОЛЬКО если есть переход или заглушка
  // Привязываем к бортшайбе (внешний край) и гребёнке
  if (isFreeFlange && bortshaibaId && needsCapOrReducer) {
    const connLength = length;
    const x1 = direction === 'left' ? startX - connLength : startX;
    const x2 = startX;

    // Для прямой заглушки привязываемся к заглушке, а не к основной трубе
    const useCapAsReference = capId != null && capType === 'straight';
    // Для горизонтальных прямых заглушек (повернуты на 90°) используем 'top' (внутренний край)
    const capElementType = 'top';
    
    // Для прямой заглушки: координата x должна быть на внутреннем крае заглушки
    const capX = useCapAsReference && capLength 
      ? (direction === 'left' ? startX + capLength / 2 : startX - capLength / 2)
      : x2;

    return {
      isHorizontal: true,
      lockOrientation: true,
      angle: 0,
      x1,
      y1: fittingCenterY,
      x2,
      y2: fittingCenterY,
      position: { x: 0, y: dimY },
      value: formatDimension(connLength),
      rawValue: connLength,
      extensionLines: [],
      precision: 1,
      textPosition: { x: 0, y: 0 },
      textFontFamily: 'Arial',
      textFontStyle: 'italic',
      textColor: '#ff0000',
      elements: [
        { objectId: bortshaibaId, objectType: 'fitting', x: x1, y: fittingCenterY, type: 'top' },
        {
          objectId: useCapAsReference ? capId : mainPipeId,
          objectType: useCapAsReference ? 'fitting' : 'cylinder',
          x: capX,
          y: fittingCenterY,
          type: useCapAsReference ? capElementType : (direction === 'left' ? 'left' : 'right'),
        },
      ],
    };
  }

  // Для приварных фланцев: размерная линия если есть переход/заглушка и промежуточная труба
  if (isWeldedFlange && intermediatePipeId && needsCapOrReducer) {
    const connLength = length;
    const x1 = direction === 'left' ? startX - connLength : startX;
    const x2 = startX;

    // Для прямой заглушки привязываемся к заглушке, а не к основной трубе
    const useCapAsReference = capId != null && capType === 'straight';
    // Для горизонтальных прямых заглушек (повернуты на 90°) используем 'top' (внутренний край)
    const capElementType = 'top';
    
    // Для прямой заглушки: координата x должна быть на внутреннем крае заглушки
    const capX = useCapAsReference && capLength 
      ? (direction === 'left' ? startX + capLength / 2 : startX - capLength / 2)
      : x2;

    return {
      isHorizontal: true,
      lockOrientation: true,
      angle: 0,
      x1,
      y1: fittingCenterY,
      x2,
      y2: fittingCenterY,
      position: { x: 0, y: dimY },
      value: formatDimension(connLength),
      rawValue: connLength,
      extensionLines: [],
      precision: 1,
      textPosition: { x: 0, y: 0 },
      textFontFamily: 'Arial',
      textFontStyle: 'italic',
      textColor: '#ff0000',
      elements: [
        { objectId: intermediatePipeId, objectType: 'cylinder', x: x1, y: fittingCenterY, type: 'left' },
        {
          objectId: useCapAsReference ? capId : mainPipeId,
          objectType: useCapAsReference ? 'fitting' : 'cylinder',
          x: capX,
          y: fittingCenterY,
          type: useCapAsReference ? capElementType : (direction === 'left' ? 'left' : 'right'),
        },
      ],
    };
  }

  // Для фланцев без перехода/заглушки: показываем размерную линию длины промежуточной трубы (если она есть)
  if (isFlange && intermediatePipeId && !needsCapOrReducer) {
    const connLength = length;
    const x1 = direction === 'left' ? startX - connLength : startX;
    const x2 = direction === 'left' ? startX : startX + connLength;

    return {
      isHorizontal: true,
      lockOrientation: true,
      angle: 0,
      x1,
      y1: fittingCenterY,
      x2,
      y2: fittingCenterY,
      position: { x: 0, y: dimY },
      value: formatDimension(connLength),
      rawValue: connLength,
      extensionLines: [],
      precision: 1,
      textPosition: { x: 0, y: 0 },
      textFontFamily: 'Arial',
      textFontStyle: 'italic',
      textColor: '#ff0000',
      elements: [
        { objectId: intermediatePipeId, objectType: 'cylinder', x: x1, y: fittingCenterY, type: 'left' },
        { objectId: intermediatePipeId, objectType: 'cylinder', x: x2, y: fittingCenterY, type: 'right' },
      ],
    };
  }

  // Для накидных гаек и шайб с наружной резьбой: НЕ показываем размерную линию, если диаметры совпадают
  // (когда нет перехода/заглушки и нет промежуточной трубы)
  if ((isNakGaika || isShaibaNarRezba) && !needsCapOrReducer && !intermediatePipeId && !capId) {
    return null; // Диаметры совпадают - не показываем размерную линию для борт-шайбы
  }

  // Для обычных фитингов (не фланцев) — показываем длину фитинга
  if (!isFlange) {
    const connLength = length;
    const x1 = direction === 'left' ? startX - connLength : startX;
    const x2 = direction === 'left' ? startX : startX + connLength;

    let elements;

    if (isPipe) {
      // Для трубы: левая точка трубы и правая точка трубы
      elements = [
        { objectId: id, objectType: 'cylinder', x: x1, y: fittingCenterY, type: 'left' },
        { objectId: id, objectType: 'cylinder', x: x2, y: fittingCenterY, type: 'right' },
      ];
    } else if ((isNakGaika && bortshaibaId) || isShaibaNarRezba) {
      // Для накидной гайки: от заглушки (если есть) до шайбы накидной гайки (размер показывает полный вылет от заглушки)
      // Для шайбы с нар. резьбой: от заглушки (если есть) до самой шайбы (размер показывает полный вылет = труба + шайба от заглушки)
      
      // Если есть заглушка - используем её как точку отсчета
      // Иначе используем гребенку (как раньше)
      const useCapAsReference = capId != null;
      
      // Для прямой заглушки привязываемся к внутреннему краю
      // Для горизонтальных прямых заглушек (повернуты на 90°) используем 'top' (внутренний край)
      // Для эллиптической заглушки привязываемся к 'top' (внешний край)
      let capElementType = 'top';
      if (useCapAsReference && capType === 'straight') {
        capElementType = 'top';
      }
      
      // Для прямой заглушки: координата x должна быть на внутреннем крае заглушки
      const capX = useCapAsReference && capType === 'straight' && capLength 
        ? (direction === 'left' ? startX + capLength / 2 : startX - capLength / 2)
        : startX;
      
      elements = [
        {
          objectId: useCapAsReference ? capId : mainPipeId,
          objectType: useCapAsReference ? 'fitting' : 'cylinder',
          x: capX,
          y: fittingCenterY,
          type: useCapAsReference ? capElementType : (direction === 'left' ? 'left' : 'right'),
        },
        {
          objectId: isShaibaNarRezba ? id : bortshaibaId,
          objectType: 'fitting',
          x: direction === 'left' ? startX - connLength : startX + connLength,
          y: fittingCenterY,
          type: 'top',
        },
      ];
    } else {
      // Для фитинга (повёрнут на -90° для left или 90° для right)
      // left: top→left, bottom→right в мировых координатах
      // right: bottom→left, top→right в мировых координатах
      elements = direction === 'left' ? [
        { objectId: id, objectType: 'fitting', x: x1, y: fittingCenterY, type: 'top' },
        { objectId: id, objectType: 'fitting', x: x2, y: fittingCenterY, type: 'bottom' },
      ] : [
        { objectId: id, objectType: 'fitting', x: x1, y: fittingCenterY, type: 'bottom' },
        { objectId: id, objectType: 'fitting', x: x2, y: fittingCenterY, type: 'top' },
      ];
    }

    return {
      isHorizontal: true,
      lockOrientation: true,
      angle: 0,
      x1,
      y1: fittingCenterY,
      x2,
      y2: fittingCenterY,
      position: { x: 0, y: dimY },
      value: formatDimension(connLength),
      rawValue: connLength,
      extensionLines: [],
      precision: 1,
      textPosition: { x: 0, y: 0 },
      textFontFamily: 'Arial',
      textFontStyle: 'italic',
      textColor: '#ff0000',
      elements,
    };
  }

  return null;
};

/**
 * Генерирует размерную линию для общей длины коллектора
 * @param {Object} params - параметры
 * @returns {Object} - объект размерной линии
 */
export const generateMainLengthDimension = (params) => {
  const {
    startX,
    startY,
    mainPipeLength,
    mainPipeDiameter,
    mainPipeId,
    bottomDimY,
    leftCapId = null,
    rightCapId = null,
    leftBortshaibaId = null,
    rightBortshaibaId = null,
    leftStraightCapLength = 0,
    rightStraightCapLength = 0,
    leftBortshaibaLength = 0,
    rightBortshaibaLength = 0,
    mainPipeStartX,
    actualMainPipeLength,
  } = params;

  const dimLeftX = startX;
  const dimRightX = startX + mainPipeLength;

  // Определяем элементы привязки для левого и правого края
  const leftDimElement = (leftCapId && leftStraightCapLength > 0)
    ? { objectId: leftCapId, objectType: 'fitting', x: dimLeftX, y: startY + mainPipeDiameter, type: 'top' }
    : (leftBortshaibaId && leftBortshaibaLength > 0)
      ? { objectId: leftBortshaibaId, objectType: 'fitting', x: dimLeftX, y: startY + mainPipeDiameter, type: 'top' }
      : { objectId: mainPipeId, objectType: 'cylinder', x: mainPipeStartX, y: startY + mainPipeDiameter, type: 'left' };

  const rightDimElement = (rightCapId && rightStraightCapLength > 0)
    ? { objectId: rightCapId, objectType: 'fitting', x: dimRightX, y: startY + mainPipeDiameter, type: 'top' }
    : (rightBortshaibaId && rightBortshaibaLength > 0)
      ? { objectId: rightBortshaibaId, objectType: 'fitting', x: dimRightX, y: startY + mainPipeDiameter, type: 'top' }
      : { objectId: mainPipeId, objectType: 'cylinder', x: mainPipeStartX + actualMainPipeLength, y: startY + mainPipeDiameter, type: 'right' };

  return {
    isHorizontal: true,
    lockOrientation: true,
    angle: 0,
    x1: dimLeftX,
    y1: startY + mainPipeDiameter,
    x2: dimRightX,
    y2: startY + mainPipeDiameter,
    position: { x: 0, y: bottomDimY },
    value: formatDimension(mainPipeLength),
    rawValue: mainPipeLength,
    extensionLines: [],
    precision: 1,
    textPosition: { x: 0, y: 0 },
    textFontFamily: 'Arial',
    textFontStyle: 'italic',
    textColor: '#ff0000',
    elements: [leftDimElement, rightDimElement],
  };
};

/**
 * Генерирует размерную линию для диаметра коллектора
 * @param {Object} params - параметры
 * @returns {Object} - объект размерной линии
 */
export const generateDiameterDimension = (params) => {
  const {
    startX,
    startY,
    mainPipeDiameter,
    mainPipeId,
    dimOffset = 40,
    hasLeftConnection = false,
    leftConnectionType = 'pipe',
    leftConnectionLength = 0,
    shape = 'round', // 'round' или 'square'
    mainPipeProfile = null, // Для квадратных профилей (например, '80x80')
    pipeSpec = null, // Спецификация трубы (для получения толщины)
  } = params;

  const dimensionLine = {
    isHorizontal: false,
    angle: 90,
    x1: startX,
    y1: startY,
    x2: startX,
    y2: startY + mainPipeDiameter,
    position: { 
      x: startX - dimOffset - (hasLeftConnection ? (leftConnectionType === 'pipe' ? leftConnectionLength : 50) : 0), 
      y: 0 
    },
    value: formatDimension(mainPipeDiameter),
    rawValue: mainPipeDiameter,
    extensionLines: [],
    precision: 1,
    textPosition: { x: 0, y: 0 },
    textFontFamily: 'Arial',
    textFontStyle: 'italic',
    textColor: '#ff0000',
    elements: [
      { objectId: mainPipeId, objectType: 'cylinder', x: startX, y: startY, type: 'top' },
      { objectId: mainPipeId, objectType: 'cylinder', x: startX, y: startY + mainPipeDiameter, type: 'bottom' },
    ],
  };

  // Для квадратных труб добавляем измененное значение (профиль вместо размера)
  if (shape === 'square' && mainPipeProfile) {
    const profileThickness = pipeSpec?.thickness || '2';
    dimensionLine.overrideEnabled = true;
    dimensionLine.overrideText = `${mainPipeProfile.replace('x', '×')} ×${profileThickness}`; // "80×80 ×2"
  } else if (pipeSpec) {
    // Для круглых труб добавляем толщину к диаметру
    const pipeThickness = typeof pipeSpec.thickness === 'string'
      ? pipeSpec.thickness.replace('x', '')
      : pipeSpec.thickness || 2;
    dimensionLine.overrideEnabled = true;
    dimensionLine.overrideText = `${mainPipeDiameter} ×${pipeThickness}`; // "60.3 ×2"
  }

  return dimensionLine;
};

/**
 * Генерирует размерную линию для расстояния между коллекторами (для распределительного)
 * @param {Object} params - параметры
 * @returns {Object} - объект размерной линии
 */
export const generateCollectorSpacingDimension = (params) => {
  const {
    startX,
    supplyY,
    returnY,
    supplyPipeId,
    returnPipeId,
    supplyPipeDiameter,
    returnPipeDiameter,
    dimOffset = 40,
  } = params;

  // Расстояние между центрами коллекторов
  const supplyCenterY = supplyY + supplyPipeDiameter / 2;
  const returnCenterY = returnY + returnPipeDiameter / 2;
  const spacing = returnCenterY - supplyCenterY;

  // Привязываемся к трубам через точку привязки 6 (left-middle)
  // Точка 6 = центр левой стороны = { x, y: y + height / 2 } = центр трубы по вертикали
  const supplyElement = { objectId: supplyPipeId, objectType: 'cylinder', x: startX, y: supplyY, idx: 6 };
  const returnElement = { objectId: returnPipeId, objectType: 'cylinder', x: startX, y: returnY, idx: 6 };

  return {
    isHorizontal: false,
    angle: 90,
    x1: startX,
    y1: supplyCenterY,
    x2: startX,
    y2: returnCenterY,
    position: { x: startX - dimOffset, y: 0 },
    value: formatDimension(spacing),
    rawValue: spacing,
    extensionLines: [],
    precision: 1,
    textPosition: { x: 0, y: 0 },
    textFontFamily: 'Arial',
    textFontStyle: 'italic',
    textColor: '#ff0000',
    // Привязываемся к фитингам (если есть) или к трубам
    elements: [supplyElement, returnElement],
  };
};

/**
 * Генерирует размерную линию для отступа первого контура
 * @param {Object} params - параметры
 * @returns {Object|null} - объект размерной линии или null
 */
export const generateFirstCircuitOffsetDimension = (params) => {
  const {
    circuitObjects,
    startX,
    // startY, // Не используется, заменено на circuitTopY
    firstCircuitOffset,
    topDimY,
    mainPipeId,
    mainPipeStartX,
    leftCapId = null,
    leftBortshaibaId = null,
    leftStraightCapLength = 0,
    leftBortshaibaLength = 0,
    oppositeCircuitObjects = null, // Контуры противоположного коллектора для сравнения
    oppositeFirstCircuitOffset = null, // Отступ первого контура противоположного коллектора
  } = params;

  if (circuitObjects.length === 0) return null;

  const firstCircuit = circuitObjects[0];
  const dimLeftX = startX;

  // Проверяем, нужно ли показывать размерную линию
  if (oppositeCircuitObjects && oppositeCircuitObjects.length > 0 && oppositeFirstCircuitOffset !== null) {
    const oppositeFirstCircuit = oppositeCircuitObjects[0];
    // Проверяем совпадают ли позиции X и отступы
    const positionsMatch = Math.abs(firstCircuit.centerX - oppositeFirstCircuit.centerX) < 0.1;
    const offsetsMatch = Math.abs(firstCircuitOffset - oppositeFirstCircuitOffset) < 0.1;
    
    // Скрываем размер только если И позиции И отступы совпадают
    if (positionsMatch && offsetsMatch) {
      return null;
    }
  }

  // Левый элемент привязки
  // Если есть прямая заглушка - привязываемся к ней (от внешнего края)
  // Если есть бортшайба - привязываемся к ней
  // Иначе - к гребёнке
  // Привязываемся на уровне верха фитинга (firstCircuit.y), чтобы пунктирная линия не шла через фитинг
  const circuitTopY = firstCircuit.y;
  
  const leftOffsetElement = (leftCapId && leftStraightCapLength > 0)
    ? { objectId: leftCapId, objectType: 'fitting', x: dimLeftX, y: circuitTopY, type: 'top' }
    : (leftBortshaibaId && leftBortshaibaLength > 0)
      ? { objectId: leftBortshaibaId, objectType: 'fitting', x: dimLeftX, y: circuitTopY, type: 'top' }
      : { objectId: mainPipeId, objectType: 'cylinder', x: mainPipeStartX, y: circuitTopY, type: 'left' };

  // Значение размера: просто отступ контура (без заглушки)
  // Отступ считается от края гребёнки, заглушка входит в общую длину
  const displayValue = firstCircuitOffset;

  return {
    isHorizontal: true,
    lockOrientation: true,
    angle: 0,
    x1: dimLeftX,
    y1: circuitTopY,
    x2: firstCircuit.centerX,
    y2: circuitTopY,
    position: { x: 0, y: topDimY },
    value: formatDimension(displayValue),
    rawValue: displayValue,
    extensionLines: [],
    precision: 1,
    textPosition: { x: 0, y: 0 },
    textFontFamily: 'Arial',
    textFontStyle: 'italic',
    textColor: '#ff0000',
    elements: [
      leftOffsetElement,
      { objectId: firstCircuit.id, objectType: firstCircuit.type, x: firstCircuit.centerX, y: circuitTopY, type: 'top' },
    ],
  };
};

/**
 * Генерирует размерную линию для отступа последнего контура
 * @param {Object} params - параметры
 * @returns {Object|null} - объект размерной линии или null
 */
export const generateLastCircuitOffsetDimension = (params) => {
  const {
    circuitObjects,
    startX,
    // startY, // Не используется, заменено на circuitTopY
    mainPipeLength,
    lastCircuitOffset,
    topDimY,
    mainPipeId,
    mainPipeStartX,
    actualMainPipeLength,
    rightCapId = null,
    rightBortshaibaId = null,
    rightStraightCapLength = 0,
    rightBortshaibaLength = 0,
    oppositeCircuitObjects = null, // Контуры противоположного коллектора для сравнения
    oppositeLastCircuitOffset = null, // Отступ последнего контура противоположного коллектора
  } = params;

  if (circuitObjects.length === 0) return null;

  const lastCircuit = circuitObjects[circuitObjects.length - 1];
  const dimRightX = startX + mainPipeLength;

  // Проверяем, нужно ли показывать размерную линию
  if (oppositeCircuitObjects && oppositeCircuitObjects.length > 0 && oppositeLastCircuitOffset !== null) {
    const oppositeLastCircuit = oppositeCircuitObjects[oppositeCircuitObjects.length - 1];
    // Проверяем совпадают ли позиции X и отступы
    const positionsMatch = Math.abs(lastCircuit.centerX - oppositeLastCircuit.centerX) < 0.1;
    const offsetsMatch = Math.abs(lastCircuitOffset - oppositeLastCircuitOffset) < 0.1;
    
    // Скрываем размер только если И позиции И отступы совпадают
    if (positionsMatch && offsetsMatch) {
      return null;
    }
  }

  // Правый элемент привязки
  // Если есть прямая заглушка - привязываемся к ней (от внешнего края)
  // Если есть бортшайба - привязываемся к ней
  // Иначе - к гребёнке
  // Привязываемся на уровне верха фитинга (lastCircuit.y), чтобы пунктирная линия не шла через фитинг
  const circuitTopY = lastCircuit.y;
  
  const rightOffsetElement = (rightCapId && rightStraightCapLength > 0)
    ? { objectId: rightCapId, objectType: 'fitting', x: dimRightX, y: circuitTopY, type: 'top' }
    : (rightBortshaibaId && rightBortshaibaLength > 0)
      ? { objectId: rightBortshaibaId, objectType: 'fitting', x: dimRightX, y: circuitTopY, type: 'top' }
      : { objectId: mainPipeId, objectType: 'cylinder', x: mainPipeStartX + actualMainPipeLength, y: circuitTopY, type: 'right' };

  // Значение размера: просто отступ контура (без заглушки)
  // Отступ считается от края гребёнки, заглушка входит в общую длину
  const displayValue = lastCircuitOffset;

  return {
    isHorizontal: true,
    lockOrientation: true,
    angle: 0,
    x1: lastCircuit.centerX,
    y1: circuitTopY,
    x2: dimRightX,
    y2: circuitTopY,
    position: { x: 0, y: topDimY },
    value: formatDimension(displayValue),
    rawValue: displayValue,
    extensionLines: [],
    precision: 1,
    textPosition: { x: 0, y: 0 },
    textFontFamily: 'Arial',
    textFontStyle: 'italic',
    textColor: '#ff0000',
    elements: [
      { objectId: lastCircuit.id, objectType: lastCircuit.type, x: lastCircuit.centerX, y: circuitTopY, type: 'top' },
      rightOffsetElement,
    ],
  };
};

/**
 * Генерирует размерные линии для расстояний между контурами
 * @param {Object} params - параметры
 * @returns {Array} - массив размерных линий
 */
export const generateCircuitSpacingDimensions = (params) => {
  const {
    circuitObjects,
    topDimY,
  } = params;

  const dimensions = [];

  for (let i = 0; i < circuitObjects.length - 1; i++) {
    const current = circuitObjects[i];
    const next = circuitObjects[i + 1];
    const spacing = next.centerX - current.centerX;

    const currentTopY = current.y;
    const nextTopY = next.y;

    dimensions.push({
      isHorizontal: true,
      lockOrientation: true,
      angle: 0,
      x1: current.centerX,
      y1: Math.min(currentTopY, nextTopY),
      x2: next.centerX,
      y2: Math.min(currentTopY, nextTopY),
      position: { x: 0, y: topDimY },
      value: formatDimension(spacing),
      rawValue: spacing,
      extensionLines: [],
      precision: 1,
      textPosition: { x: 0, y: 0 },
      textFontFamily: 'Arial',
      textFontStyle: 'italic',
      textColor: '#ff0000',
      elements: [
        { objectId: current.id, objectType: current.type, x: current.centerX, y: currentTopY, type: 'top' },
        { objectId: next.id, objectType: next.type, x: next.centerX, y: nextTopY, type: 'top' },
      ],
    });
  }

  return dimensions;
};

/**
 * Генерирует размерные линии для расстояний между парными контурами (для распределительного коллектора)
 * В распределительном коллекторе каждый контур = пара патрубков (короткий + длинный)
 * circuitObjects содержит: [контур1-патрубок1, контур1-патрубок2, контур2-патрубок1, контур2-патрубок2, ...]
 * Размерные линии рисуются:
 * 1. Между патрубками внутри контура (межосевое контура)
 * 2. Между последним патрубком одного контура и первым патрубком следующего (расстояние между контурами)
 * @param {Object} params - параметры
 * @returns {Array} - массив размерных линий
 */
export const generatePairedCircuitSpacingDimensions = (params) => {
  const {
    circuitObjects,
    topDimY,
    oppositeCircuitObjects = null, // Контуры противоположного коллектора для сравнения
  } = params;

  const dimensions = [];

  // Проходим по всем патрубкам и рисуем размерные линии между соседними
  // Каждый контур состоит из 2 патрубков, поэтому индексы: 0-1 (контур 0), 2-3 (контур 1), и т.д.
  for (let i = 0; i < circuitObjects.length - 1; i++) {
    const current = circuitObjects[i];
    const next = circuitObjects[i + 1];
    
    if (!current || !next) continue;
    
    const spacing = next.centerX - current.centerX;
    const currentTopY = current.y;
    const nextTopY = next.y;

    // Определяем тип размерной линии:
    // - Если i четный (0, 2, 4...), то это расстояние внутри контура (между первым и вторым патрубком)
    // - Если i нечетный (1, 3, 5...), то это расстояние между контурами (между вторым патрубком текущего и первым следующего)
    const isWithinCircuit = i % 2 === 0;
    
    // Проверяем, нужно ли показывать размерную линию
    // Если есть противоположный коллектор, проверяем совпадают ли расстояния И позиции
    let shouldShow = true;
    if (oppositeCircuitObjects && i < oppositeCircuitObjects.length - 1) {
      const oppositeCurrent = oppositeCircuitObjects[i];
      const oppositeNext = oppositeCircuitObjects[i + 1];
      if (oppositeCurrent && oppositeNext) {
        const oppositeSpacing = oppositeNext.centerX - oppositeCurrent.centerX;
        // Проверяем совпадают ли позиции X (патрубки друг под другом)
        const positionsMatch = Math.abs(current.centerX - oppositeCurrent.centerX) < 0.1 && 
                               Math.abs(next.centerX - oppositeNext.centerX) < 0.1;
        // Проверяем совпадают ли расстояния
        const spacingsMatch = Math.abs(spacing - oppositeSpacing) < 0.1;
        
        // Скрываем размер только если И позиции И расстояния совпадают
        if (positionsMatch && spacingsMatch) {
          shouldShow = false;
        }
      }
    }
    
    if (!shouldShow) continue;
    
    dimensions.push({
      isHorizontal: true,
      lockOrientation: true,
      angle: 0,
      x1: current.centerX,
      y1: Math.min(currentTopY, nextTopY),
      x2: next.centerX,
      y2: Math.min(currentTopY, nextTopY),
      position: { x: 0, y: topDimY },
      value: formatDimension(spacing),
      rawValue: spacing,
      extensionLines: [],
      precision: 1,
      textPosition: { x: 0, y: 0 },
      textFontFamily: 'Arial',
      textFontStyle: 'italic',
      textColor: '#ff0000',
      elements: [
        { objectId: current.id, objectType: current.type, x: current.centerX, y: currentTopY, type: 'top' },
        { objectId: next.id, objectType: next.type, x: next.centerX, y: nextTopY, type: 'top' },
      ],
      // Добавляем метку для отладки
      _isWithinCircuit: isWithinCircuit,
    });
  }

  return dimensions;
};

/**
 * Генерирует размерные линии для вылетов контуров (вертикальные)
 * @param {Object} params - параметры
 * @returns {Array} - массив размерных линий
 */
export const generateCircuitLengthDimensions = (params) => {
  const {
    circuitObjects,
    circuits = [],
    mainPipeId = null, // ID основной трубы коллектора
    mainPipeY = null, // Y-координата коллектора
    mainPipeDiameter = null, // Диаметр коллектора
    direction = 'up', // Направление контура ('up' или 'down')
  } = params;

  const dimensions = [];
  const shownCircuitKeys = new Set();

  circuitObjects.forEach((circuit, index) => {
    // Защита от undefined
    if (!circuit || !circuit.id) return;
    
    const circuitData = circuits[index] || {};
    let uniqueKey;

    if (circuit.isFlange) {
      uniqueKey = `flange-${circuit.height}`;
    } else if (circuit.type === 'fitting' && circuitData.fittingId) {
      uniqueKey = `fitting-${circuitData.fittingId}-${circuit.height}`;
    } else {
      uniqueKey = `pipe-${circuitData.diameter || circuit.width}-${circuit.height}`;
    }

    // Пропускаем если такой тип уже показан
    if (shownCircuitKeys.has(uniqueKey)) return;
    shownCircuitKeys.add(uniqueKey);

    // Для фланцев используем специальные объекты привязки
    const topElementId = circuit.topObjectId || circuit.id;
    const topElementType = circuit.topObjectType || circuit.type;
    const bottomElementId = circuit.bottomObjectId || circuit.id;
    const bottomElementType = circuit.bottomObjectType || circuit.type;

    // Проверяем, является ли этот контур сквозным дополнительным выходом
    const outletType = circuitData.outletType || 'short';
    const isThrough = outletType === 'through';
    
    // Для сквозных выходов модифицируем привязку конца размерной линии к трубе коллектора
    let modifiedBottomId = bottomElementId;
    let modifiedBottomType = bottomElementType;
    let modifiedY2 = circuit.y + circuit.height;
    let modifiedBottomAttachType = circuit.bottomAttachSide || 'bottom';
    let overrideEnabled = false;
    let overrideText = null;
    
    if (isThrough && mainPipeId && mainPipeY !== null && mainPipeDiameter !== null) {
      // Для сквозных выходов привязываем к трубе коллектора
      modifiedBottomId = mainPipeId;
      modifiedBottomType = 'cylinder';
      
      // Определяем реальное направление выхода (может отличаться от direction параметра)
      const outletDirection = circuitData.direction || direction;
      
      // Вычисляем Y-координату привязки к трубе и тип привязки
      if (outletDirection === 'up') {
        // Для выхода вверх привязываемся к верхней стороне трубы (низ размерной линии)
        modifiedY2 = mainPipeY;
        modifiedBottomAttachType = 'top'; // Привязка к верхней стороне трубы
      } else {
        // Для выхода вниз привязываемся к нижней стороне трубы (верх размерной линии)
        modifiedY2 = mainPipeY + mainPipeDiameter;
        modifiedBottomAttachType = 'bottom'; // Привязка к нижней стороне трубы
      }
      
    }

    dimensions.push({
      isHorizontal: false,
      angle: 90,
      x1: circuit.x + circuit.width,
      y1: circuit.y,
      x2: circuit.x + circuit.width,
      y2: modifiedY2,
      position: { x: circuit.x + circuit.width + 35, y: 0 },
      value: formatDimension(circuit.height),
      rawValue: circuit.height,
      overrideEnabled: overrideEnabled,
      overrideText: overrideText,
      extensionLines: [],
      precision: 1,
      textPosition: { x: 0, y: 0 },
      textFontFamily: 'Arial',
      textFontStyle: 'italic',
      textColor: '#ff0000',
      elements: [
        { objectId: topElementId, objectType: topElementType, x: circuit.x + circuit.width, y: circuit.y, type: 'top' },
        { objectId: modifiedBottomId, objectType: modifiedBottomType, x: circuit.x + circuit.width, y: modifiedY2, type: modifiedBottomAttachType },
      ],
    });
  });

  return dimensions;
};

/**
 * Генерирует размерные линии для вылетов парных контуров (для распределительного коллектора)
 * В распределительном коллекторе каждый контур = пара патрубков (короткий + длинный)
 * Показываем размерные линии только для коротких патрубков
 * @param {Object} params - параметры
 * @param {boolean} params.isReturnCollector - true для обратного коллектора (порядок: длинный, короткий)
 * @returns {Array} - массив размерных линий
 */
export const generatePairedCircuitLengthDimensions = (params) => {
  const {
    circuitObjects,
    circuits = [],
  } = params;

  const dimensions = [];
  const shownCircuitKeys = new Set();

  // Проходим по всем патрубкам
  circuitObjects.forEach((circuit, index) => {
    // Определяем индекс контура (каждый контур = 2 патрубка)
    const circuitIndex = Math.floor(index / 2);
    const circuitData = circuits[circuitIndex] || {};
    
    // Пропускаем длинные патрубки - показываем размеры только для коротких
    // Используем метку isShort, которая учитывает swap
    if (!circuit.isShort) return;
    
    let uniqueKey;

    if (circuit.isFlange) {
      uniqueKey = `flange-${circuit.height}`;
    } else if (circuit.type === 'fitting' && circuitData.fittingId) {
      uniqueKey = `fitting-${circuitData.fittingId}-${circuit.height}`;
    } else {
      uniqueKey = `pipe-${circuitData.diameter || circuit.width}-${circuit.height}`;
    }

    // Пропускаем если такой тип уже показан
    if (shownCircuitKeys.has(uniqueKey)) return;
    shownCircuitKeys.add(uniqueKey);

    // Для фланцев используем специальные объекты привязки
    const topElementId = circuit.topObjectId || circuit.id;
    const topElementType = circuit.topObjectType || circuit.type;
    const bottomElementId = circuit.bottomObjectId || circuit.id;
    const bottomElementType = circuit.bottomObjectType || circuit.type;

    dimensions.push({
      isHorizontal: false,
      angle: 90,
      x1: circuit.x + circuit.width,
      y1: circuit.y,
      x2: circuit.x + circuit.width,
      y2: circuit.y + circuit.height,
      position: { x: circuit.x + circuit.width + 35, y: 0 },
      value: formatDimension(circuit.height),
      rawValue: circuit.height,
      extensionLines: [],
      precision: 1,
      textPosition: { x: 0, y: 0 },
      textFontFamily: 'Arial',
      textFontStyle: 'italic',
      textColor: '#ff0000',
      elements: [
        { objectId: topElementId, objectType: topElementType, x: circuit.x + circuit.width, y: circuit.y, type: 'top' },
        { objectId: bottomElementId, objectType: bottomElementType, x: circuit.x + circuit.width, y: circuit.y + circuit.height, type: circuit.bottomAttachSide || 'bottom' },
      ],
    });
  });

  return dimensions;
};

/**
 * Генерирует все размерные линии для этажного коллектора
 * @param {Object} params - параметры
 * @returns {Array} - массив размерных линий
 */
export const generateFloorCollectorDimensions = (params) => {
  const {
    startY,
    mainPipeDiameter,
    circuitObjects,
    circuits,
    dimOffset = 40,
  } = params;

  const dimensionLines = [];

  // Вычисляем уровни для размерных линий
  const minCircuitTopY = circuitObjects.length > 0
    ? Math.min(...circuitObjects.map(c => c.y))
    : startY;
  const topDimY = minCircuitTopY - dimOffset * 2;
  const bottomDimY = startY + mainPipeDiameter + dimOffset * 2;

  // 1. Общая длина
  dimensionLines.push(generateMainLengthDimension({
    ...params,
    bottomDimY,
  }));

  // 2. Диаметр
  dimensionLines.push(generateDiameterDimension(params));

  // 3. Отступ первого контура
  const firstOffsetDim = generateFirstCircuitOffsetDimension({
    ...params,
    topDimY,
  });
  if (firstOffsetDim) dimensionLines.push(firstOffsetDim);

  // 4. Расстояния между контурами
  dimensionLines.push(...generateCircuitSpacingDimensions({
    circuitObjects,
    topDimY,
  }));

  // 5. Отступ последнего контура
  const lastOffsetDim = generateLastCircuitOffsetDimension({
    ...params,
    topDimY,
  });
  if (lastOffsetDim) dimensionLines.push(lastOffsetDim);

  // 6. Вылеты контуров
  dimensionLines.push(...generateCircuitLengthDimensions({
    circuitObjects,
    circuits,
  }));

  return dimensionLines;
};

/**
 * Генерирует все размерные линии для распределительного коллектора
 * @param {Object} params - параметры
 * @returns {Array} - массив размерных линий
 */
export const generateDistributionCollectorDimensions = (params) => {
  const {
    startX,
    supplyY,
    returnY,
    supplyPipeId,
    returnPipeId,
    supplyPipeDiameter,
    returnPipeDiameter,
    supplyPipeLength,
    returnPipeLength,
    supplyCircuitObjects,
    returnCircuitObjects,
    supplyFirstCircuitOffset,
    supplyLastCircuitOffset,
    returnFirstCircuitOffset,
    returnLastCircuitOffset,
    supplyCircuits,
    returnCircuits,
    dimOffset = 40,
    // Информация о торцевых подключениях
    supplyLeftConnectionInfo,
    supplyRightConnectionInfo,
    returnLeftConnectionInfo,
    returnRightConnectionInfo,
    // Информация о заглушках
    supplyLeftCapId,
    supplyRightCapId,
    returnLeftCapId,
    returnRightCapId,
    // Длины прямых заглушек
    supplyLeftStraightCapLength = 0,
    supplyRightStraightCapLength = 0,
    returnLeftStraightCapLength = 0,
    returnRightStraightCapLength = 0,
    // Длины бортшайб (для размерных линий и отступов)
    supplyLeftBortshaibaLength = 0,
    supplyRightBortshaibaLength = 0,
    returnLeftBortshaibaLength = 0,
    returnRightBortshaibaLength = 0,
    // Фактические позиции гребёнок
    supplyPipeStartX,
    returnPipeStartX,
    actualSupplyPipeLength,
    actualReturnPipeLength,
    // Дополнительные выходы
    supplyAdditionalOutletObjects = [],
    returnAdditionalOutletObjects = [],
    supplyAdditionalOutlets = [],
    returnAdditionalOutlets = [],
    // Форма и профили (для квадратных коллекторов)
    shape = 'round', // 'round' или 'square'
    supplyPipeProfile = null, // Для квадратных (например, '80x80')
    returnPipeProfile = null, // Для квадратных (например, '80x80')
    supplyPipeSpec = null, // Спецификация подающей трубы (для толщины)
    returnPipeSpec = null, // Спецификация обратной трубы (для толщины)
    // Компактный режим
    isCompact = false,
    pipeSpacing = 200,
  } = params;

  const dimensionLines = [];

  // Вычисляем уровни для размерных линий
  // Учитываем как парные контуры, так и дополнительные выходы
  const allSupplyObjects = [...supplyCircuitObjects, ...supplyAdditionalOutletObjects].filter(obj => obj && obj.y != null && obj.height != null);
  const allReturnObjects = [...returnCircuitObjects, ...returnAdditionalOutletObjects].filter(obj => obj && obj.y != null && obj.height != null);
  
  const minSupplyTopY = allSupplyObjects.length > 0
    ? Math.min(...allSupplyObjects.map(c => c.y))
    : supplyY;
  const maxReturnBottomY = allReturnObjects.length > 0
    ? Math.max(...allReturnObjects.map(c => c.y + c.height))
    : returnY + returnPipeDiameter;

  const topDimY = minSupplyTopY - dimOffset * 2;
  const bottomDimY = maxReturnBottomY + dimOffset * 3; // Отступ для нижних размерных линий отступов
  const topTotalLengthDimY = minSupplyTopY - dimOffset * 4; // Для общего размера сверху (если подача длиннее)

  // 1. Общая длина коллектора (фиксированная, заглушки входят в эту длину)
  // Всегда показываем размер для подающего коллектора (сверху)
    dimensionLines.push(generateMainLengthDimension({
      startX,
      startY: supplyY,
      mainPipeLength: supplyPipeLength,
      mainPipeDiameter: supplyPipeDiameter,
      mainPipeId: supplyPipeId,
      bottomDimY: topTotalLengthDimY, // Используем верхний отступ
      mainPipeStartX: supplyPipeStartX || startX,
      actualMainPipeLength: actualSupplyPipeLength || supplyPipeLength,
      leftCapId: supplyLeftCapId,
      rightCapId: supplyRightCapId,
      leftStraightCapLength: supplyLeftStraightCapLength,
      rightStraightCapLength: supplyRightStraightCapLength,
      leftBortshaibaId: supplyLeftConnectionInfo?.bortshaibaId,
      rightBortshaibaId: supplyRightConnectionInfo?.bortshaibaId,
      leftBortshaibaLength: supplyLeftBortshaibaLength,
      rightBortshaibaLength: supplyRightBortshaibaLength,
    }));

  // 2. Диаметры коллекторов (не показываем в компактном режиме)
  if (!isCompact) {
    // Если диаметры одинаковые - показываем только для нижнего (обратного)
    if (supplyPipeDiameter !== returnPipeDiameter) {
      dimensionLines.push(generateDiameterDimension({
        startX,
        startY: supplyY,
        mainPipeDiameter: supplyPipeDiameter,
        mainPipeId: supplyPipeId,
        dimOffset,
        shape,
        mainPipeProfile: supplyPipeProfile,
        pipeSpec: supplyPipeSpec,
      }));
    }

    dimensionLines.push(generateDiameterDimension({
      startX,
      startY: returnY,
      mainPipeDiameter: returnPipeDiameter,
      mainPipeId: returnPipeId,
      dimOffset,
      shape,
      mainPipeProfile: returnPipeProfile,
      pipeSpec: returnPipeSpec,
    }));
  }

  // 3. Расстояние между коллекторами / Общая высота (в компактном режиме)
  if (isCompact) {
    // В компактном режиме показываем общую высоту от верха листа до низа профиля
    // Высота профиля (обратный)
    const returnHeight = returnPipeProfile ? parseInt(returnPipeProfile.split('x')[1]) : returnPipeDiameter;
    // Общая высота = pipeSpacing
    const totalHeight = pipeSpacing;
    
    // Определяем общий размер для отображения (60x60 или 80x80)
    // Лист 60x30 -> общий размер 60x60, лист 80x40 -> общий размер 80x80
    const sheetWidth = supplyPipeProfile ? parseInt(supplyPipeProfile.split('x')[0]) : 80;
    const overrideText = `${sheetWidth}×${sheetWidth}`;
    
    // Привязываемся к верху подающего (лист) и низу обратного (профиль)
    const supplyTopElement = { objectId: supplyPipeId, objectType: 'cylinder', x: startX, y: supplyY, idx: 0 }; // top-left
    const returnBottomElement = { objectId: returnPipeId, objectType: 'cylinder', x: startX, y: returnY, idx: 3 }; // bottom-left
    
    dimensionLines.push({
      isHorizontal: false,
      angle: 90,
      x1: startX,
      y1: supplyY,
      x2: startX,
      y2: returnY + returnHeight,
      position: { x: startX - dimOffset, y: 0 },
      value: formatDimension(totalHeight),
      rawValue: totalHeight,
      overrideEnabled: true,
      overrideText: overrideText, // "60×60" или "80×80"
      extensionLines: [],
      precision: 0,
      textPosition: { x: 0, y: 0 },
      textFontFamily: 'Arial',
      textFontStyle: 'italic',
      textColor: '#ff0000',
      elements: [supplyTopElement, returnBottomElement],
    });
  } else {
    // В обычном режиме показываем межосевое расстояние
    dimensionLines.push(generateCollectorSpacingDimension({
      startX,
      supplyY,
      returnY,
      supplyPipeId,
      returnPipeId,
      supplyPipeDiameter,
      returnPipeDiameter,
      dimOffset,
    }));
  }

  // 4. Отступ первого контура подающего коллектора
  // Размер от внешнего края левой заглушки/бортшайбы (или от startX если их нет)
  const supplyFirstOffsetDim = generateFirstCircuitOffsetDimension({
    circuitObjects: supplyCircuitObjects,
    startX, // От внешнего края (включая заглушку и бортшайбу)
    startY: supplyY,
    firstCircuitOffset: supplyFirstCircuitOffset,
    topDimY,
    mainPipeId: supplyPipeId,
    mainPipeStartX: supplyPipeStartX || startX,
    leftCapId: supplyLeftCapId,
    leftStraightCapLength: supplyLeftStraightCapLength,
    leftBortshaibaId: supplyLeftConnectionInfo?.bortshaibaId,
    leftBortshaibaLength: supplyLeftBortshaibaLength,
  });
  if (supplyFirstOffsetDim) dimensionLines.push(supplyFirstOffsetDim);

  // 5. Расстояния между контурами (по подающему)
  // Используем специальную функцию для парных контуров
  dimensionLines.push(...generatePairedCircuitSpacingDimensions({
    circuitObjects: supplyCircuitObjects,
    topDimY,
    oppositeCircuitObjects: null, // Для верхнего коллектора всегда показываем размеры
  }));

  // 6. Отступ последнего контура подающего коллектора
  // Размер до внешнего края правой заглушки/бортшайбы (или до конца трубы если их нет)
  const supplyLastOffsetDim = generateLastCircuitOffsetDimension({
    circuitObjects: supplyCircuitObjects,
    startX, // От внешнего края (включая заглушку и бортшайбу)
    startY: supplyY,
    mainPipeLength: supplyPipeLength,
    lastCircuitOffset: supplyLastCircuitOffset,
    topDimY,
    mainPipeId: supplyPipeId,
    mainPipeStartX: supplyPipeStartX || startX,
    actualMainPipeLength: actualSupplyPipeLength || supplyPipeLength,
    rightCapId: supplyRightCapId,
    rightStraightCapLength: supplyRightStraightCapLength,
    rightBortshaibaId: supplyRightConnectionInfo?.bortshaibaId,
    rightBortshaibaLength: supplyRightBortshaibaLength,
  });
  if (supplyLastOffsetDim) dimensionLines.push(supplyLastOffsetDim);

  // 7. Отступ первого контура обратного коллектора
  // Размер от внешнего края левой заглушки/бортшайбы (или от startX если их нет)
  const returnFirstOffsetDim = generateFirstCircuitOffsetDimension({
    circuitObjects: returnCircuitObjects,
    startX, // От внешнего края (включая заглушку и бортшайбу)
    startY: returnY,
    firstCircuitOffset: returnFirstCircuitOffset,
    topDimY: bottomDimY,
    mainPipeId: returnPipeId,
    mainPipeStartX: returnPipeStartX || startX,
    oppositeCircuitObjects: supplyCircuitObjects, // Передаем контуры подачи для сравнения
    oppositeFirstCircuitOffset: supplyFirstCircuitOffset, // Передаем отступ подачи
    leftCapId: returnLeftCapId,
    leftStraightCapLength: returnLeftStraightCapLength,
    leftBortshaibaId: returnLeftConnectionInfo?.bortshaibaId,
    leftBortshaibaLength: returnLeftBortshaibaLength,
  });
  if (returnFirstOffsetDim) dimensionLines.push(returnFirstOffsetDim);

  // 8. Расстояния между контурами обратного коллектора
  // Скрываем размеры, которые совпадают с верхним коллектором
  dimensionLines.push(...generatePairedCircuitSpacingDimensions({
    circuitObjects: returnCircuitObjects,
    topDimY: bottomDimY,
    oppositeCircuitObjects: supplyCircuitObjects, // Передаем контуры подачи для сравнения
  }));

  // 9. Отступ последнего контура обратного коллектора
  // Размер до внешнего края правой заглушки/бортшайбы (или до конца трубы если их нет)
  const returnLastOffsetDim = generateLastCircuitOffsetDimension({
    circuitObjects: returnCircuitObjects,
    startX, // От внешнего края (включая заглушку и бортшайбу)
    startY: returnY,
    mainPipeLength: returnPipeLength,
    lastCircuitOffset: returnLastCircuitOffset,
    topDimY: bottomDimY,
    mainPipeId: returnPipeId,
    mainPipeStartX: returnPipeStartX || startX,
    actualMainPipeLength: actualReturnPipeLength || returnPipeLength,
    oppositeCircuitObjects: supplyCircuitObjects, // Передаем контуры подачи для сравнения
    oppositeLastCircuitOffset: supplyLastCircuitOffset, // Передаем отступ подачи
    rightCapId: returnRightCapId,
    rightStraightCapLength: returnRightStraightCapLength,
    rightBortshaibaId: returnRightConnectionInfo?.bortshaibaId,
    rightBortshaibaLength: returnRightBortshaibaLength,
  });
  if (returnLastOffsetDim) dimensionLines.push(returnLastOffsetDim);

  // 10. Вылеты подающих контуров
  // Используем специальную функцию для парных контуров
  dimensionLines.push(...generatePairedCircuitLengthDimensions({
    circuitObjects: supplyCircuitObjects,
    circuits: supplyCircuits.map(c => ({
      fittingId: c.fitting,
    })),
  }));

  // 11. Вылеты обратных контуров
  // Используем специальную функцию для парных контуров
  // Для обратного коллектора порядок патрубков обратный: длинный, короткий
  dimensionLines.push(...generatePairedCircuitLengthDimensions({
    circuitObjects: returnCircuitObjects,
    circuits: returnCircuits.map(c => ({
      fittingId: c.fitting,
    })),
    isReturnCollector: true,
  }));

  // 12. Вылеты дополнительных выходов подающего коллектора
  if (supplyAdditionalOutletObjects && supplyAdditionalOutletObjects.length > 0) {
    // Вылеты фитингов (длины)
    dimensionLines.push(...generateCircuitLengthDimensions({
      circuitObjects: supplyAdditionalOutletObjects,
      circuits: supplyAdditionalOutlets || [],
      mainPipeId: supplyPipeId,
      mainPipeY: supplyY,
      mainPipeDiameter: supplyPipeDiameter,
      shape: shape,
      direction: 'up',
    }));
    
    // Размерные линии расстояния от торца (только для positionType === 'distance')
    const additionalOutletsTopDimY = topDimY + dimOffset * 2;
    
    (supplyAdditionalOutlets || []).forEach((outlet, index) => {
      if (outlet.positionType === 'distance' && outlet.distanceFromEdge != null) {
        const outletObj = supplyAdditionalOutletObjects[index];
        if (!outletObj || !outletObj.id || !outletObj.centerX) return;
        
        const displayValue = outlet.distanceFromEdge;
        const direction = outlet.distanceDirection || 'left';
        
        // Определяем Y-координату точки подключения к гребенке
        // Для направления "вверх" это нижняя точка фитинга (outletObj.y + outletObj.height)
        // Для направления "вниз" это верхняя точка фитинга (outletObj.y)
        const connectionY = outlet.direction === 'up' ? (outletObj.y + outletObj.height) : outletObj.y;
        
        if (direction === 'right') {
          // Отсчёт справа - от фитинга до правой заглушки/бортшайбы/трубы
          const rightEdgeX = startX + supplyPipeLength;
          const x1 = outletObj.centerX;
          const x2 = rightEdgeX;
          
          // Приоритет привязки: прямая заглушка > бортшайба > труба
          let rightElement;
          if (supplyRightCapId && supplyRightStraightCapLength > 0) {
            rightElement = { objectId: supplyRightCapId, objectType: 'fitting', x: rightEdgeX - supplyRightStraightCapLength, y: connectionY, type: 'top' };
          } else if (supplyRightConnectionInfo?.bortshaibaId && supplyRightBortshaibaLength > 0) {
            rightElement = { objectId: supplyRightConnectionInfo.bortshaibaId, objectType: 'fitting', x: rightEdgeX, y: connectionY, type: 'top' };
          } else {
            rightElement = { objectId: supplyPipeId, objectType: 'cylinder', x: supplyPipeStartX + actualSupplyPipeLength, y: connectionY, type: 'right' };
          }
          
          dimensionLines.push({
            id: `dim-supply-outlet-${index}-${Date.now()}`,
            isHorizontal: true,
            lockOrientation: true,
            angle: 0,
            x1,
            y1: connectionY,
            x2,
            y2: connectionY,
            position: { x: 0, y: additionalOutletsTopDimY },
            value: formatDimension(displayValue),
            rawValue: displayValue,
            precision: 1,
            elements: [
              { objectId: outletObj.topObjectId || outletObj.id, objectType: outletObj.topObjectType || 'fitting', x: outletObj.centerX, y: additionalOutletsTopDimY, type: 'top' },
              rightElement,
            ],
          });
        } else {
          // Отсчёт слева - от левой заглушки/бортшайбы/трубы до фитинга
          const x1 = startX;
          const x2 = outletObj.centerX;
          
          // Приоритет привязки: прямая заглушка > бортшайба > труба
          let leftElement;
          if (supplyLeftCapId && supplyLeftStraightCapLength > 0) {
            leftElement = { objectId: supplyLeftCapId, objectType: 'fitting', x: startX, y: connectionY, type: 'top' };
          } else if (supplyLeftConnectionInfo?.bortshaibaId && supplyLeftBortshaibaLength > 0) {
            leftElement = { objectId: supplyLeftConnectionInfo.bortshaibaId, objectType: 'fitting', x: startX, y: connectionY, type: 'top' };
          } else {
            leftElement = { objectId: supplyPipeId, objectType: 'cylinder', x: supplyPipeStartX, y: connectionY, type: 'left' };
          }
          
          dimensionLines.push({
            id: `dim-supply-outlet-${index}-${Date.now()}`,
            isHorizontal: true,
            lockOrientation: true,
            angle: 0,
            x1,
            y1: connectionY,
            x2,
            y2: connectionY,
            position: { x: 0, y: additionalOutletsTopDimY },
            value: formatDimension(displayValue),
            rawValue: displayValue,
            precision: 1,
            elements: [
              leftElement,
              { objectId: outletObj.topObjectId || outletObj.id, objectType: outletObj.topObjectType || 'fitting', x: outletObj.centerX, y: additionalOutletsTopDimY, type: 'top' },
            ],
          });
        }
      }
    });
  }

  // 13. Вылеты дополнительных выходов обратного коллектора
  if (returnAdditionalOutletObjects && returnAdditionalOutletObjects.length > 0) {
    // Вылеты фитингов (длины)
    dimensionLines.push(...generateCircuitLengthDimensions({
      circuitObjects: returnAdditionalOutletObjects,
      circuits: returnAdditionalOutlets || [],
      mainPipeId: returnPipeId,
      mainPipeY: returnY,
      mainPipeDiameter: returnPipeDiameter,
      shape: shape,
      direction: 'down',
    }));
    
    // Размерные линии расстояния от торца (только для positionType === 'distance')
    const additionalOutletsBottomDimY = bottomDimY - dimOffset * 2;
    
    (returnAdditionalOutlets || []).forEach((outlet, index) => {
      if (outlet.positionType === 'distance' && outlet.distanceFromEdge != null) {
        const outletObj = returnAdditionalOutletObjects[index];
        if (!outletObj || !outletObj.id || !outletObj.centerX) return;
        
        const displayValue = outlet.distanceFromEdge;
        const direction = outlet.distanceDirection || 'left';
        
        // Определяем Y-координату точки подключения к гребенке
        // Для направления "вниз" это верхняя точка фитинга (outletObj.y)
        // Для направления "вверх" это нижняя точка фитинга (outletObj.y + outletObj.height)
        const connectionY = outlet.direction === 'down' ? outletObj.y : (outletObj.y + outletObj.height);
        
        if (direction === 'right') {
          // Отсчёт справа - от фитинга до правой заглушки/бортшайбы/трубы
          const rightEdgeX = startX + returnPipeLength;
          const x1 = outletObj.centerX;
          const x2 = rightEdgeX;
          
          // Приоритет привязки: прямая заглушка > бортшайба > труба
          let rightElement;
          if (returnRightCapId && returnRightStraightCapLength > 0) {
            rightElement = { objectId: returnRightCapId, objectType: 'fitting', x: rightEdgeX - returnRightStraightCapLength, y: connectionY, type: 'top' };
          } else if (returnRightConnectionInfo?.bortshaibaId && returnRightBortshaibaLength > 0) {
            rightElement = { objectId: returnRightConnectionInfo.bortshaibaId, objectType: 'fitting', x: rightEdgeX, y: connectionY, type: 'top' };
          } else {
            rightElement = { objectId: returnPipeId, objectType: 'cylinder', x: returnPipeStartX + actualReturnPipeLength, y: connectionY, type: 'right' };
          }
          
          dimensionLines.push({
            id: `dim-return-outlet-${index}-${Date.now()}`,
            isHorizontal: true,
            lockOrientation: true,
            angle: 0,
            x1,
            y1: connectionY,
            x2,
            y2: connectionY,
            position: { x: 0, y: additionalOutletsBottomDimY },
            value: formatDimension(displayValue),
            rawValue: displayValue,
            precision: 1,
            elements: [
              { objectId: outletObj.bottomObjectId || outletObj.id, objectType: outletObj.bottomObjectType || 'fitting', x: outletObj.centerX, y: additionalOutletsBottomDimY, type: outletObj.bottomAttachSide || 'bottom' },
              rightElement,
            ],
          });
        } else {
          // Отсчёт слева - от левой заглушки/бортшайбы/трубы до фитинга
          const x1 = startX;
          const x2 = outletObj.centerX;
          
          // Приоритет привязки: прямая заглушка > бортшайба > труба
          let leftElement;
          if (returnLeftCapId && returnLeftStraightCapLength > 0) {
            leftElement = { objectId: returnLeftCapId, objectType: 'fitting', x: startX, y: connectionY, type: 'top' };
          } else if (returnLeftConnectionInfo?.bortshaibaId && returnLeftBortshaibaLength > 0) {
            leftElement = { objectId: returnLeftConnectionInfo.bortshaibaId, objectType: 'fitting', x: startX, y: connectionY, type: 'top' };
          } else {
            leftElement = { objectId: returnPipeId, objectType: 'cylinder', x: returnPipeStartX, y: connectionY, type: 'left' };
          }
          
          dimensionLines.push({
            id: `dim-return-outlet-${index}-${Date.now()}`,
            isHorizontal: true,
            lockOrientation: true,
            angle: 0,
            x1,
            y1: connectionY,
            x2,
            y2: connectionY,
            position: { x: 0, y: additionalOutletsBottomDimY },
            value: formatDimension(displayValue),
            rawValue: displayValue,
            precision: 1,
            elements: [
              leftElement,
              { objectId: outletObj.bottomObjectId || outletObj.id, objectType: outletObj.bottomObjectType || 'fitting', x: outletObj.centerX, y: additionalOutletsBottomDimY, type: outletObj.bottomAttachSide || 'bottom' },
            ],
          });
        }
      }
    });
  }

  // 14. Вылеты торцевых подключений
  // Подающий коллектор (верхний) - размерные линии ВВЕРХ (topDimY)
  // Подающий коллектор - левое подключение
  if (supplyLeftConnectionInfo) {
    const dim = generateConnectionDimensionLine({
      connectionInfo: supplyLeftConnectionInfo,
      mainPipeId: supplyPipeId,
      mainPipeDiameter: supplyPipeDiameter,
      startY: supplyY,
      direction: 'left',
      dimY: topDimY,
    });
    if (dim) dimensionLines.push(dim);
  }

  // Подающий коллектор - правое подключение
  if (supplyRightConnectionInfo) {
    const dim = generateConnectionDimensionLine({
      connectionInfo: supplyRightConnectionInfo,
      mainPipeId: supplyPipeId,
      mainPipeDiameter: supplyPipeDiameter,
      startY: supplyY,
      direction: 'right',
      dimY: topDimY,
    });
    if (dim) dimensionLines.push(dim);
  }

  // Обратный коллектор (нижний) - размерные линии ВНИЗ (bottomDimY)
  // Обратный коллектор - левое подключение
  // Проверяем, нужно ли показывать размерную линию
  // Скрываем размер только если фитинг И длина совпадают с подающим коллектором
  if (returnLeftConnectionInfo) {
    let shouldShow = true;
    
    if (supplyLeftConnectionInfo) {
      const fittingsMatch = returnLeftConnectionInfo.fittingKey === supplyLeftConnectionInfo.fittingKey;
      const lengthsMatch = Math.abs(returnLeftConnectionInfo.length - supplyLeftConnectionInfo.length) < 0.1;
      
      // Скрываем размер только если И фитинги И длины совпадают
      if (fittingsMatch && lengthsMatch) {
        shouldShow = false;
      }
    }
    
    if (shouldShow) {
      const dim = generateConnectionDimensionLine({
        connectionInfo: returnLeftConnectionInfo,
        mainPipeId: returnPipeId,
        mainPipeDiameter: returnPipeDiameter,
        startY: returnY,
        direction: 'left',
        dimY: bottomDimY,
      });
      if (dim) dimensionLines.push(dim);
    }
  }

  // Обратный коллектор - правое подключение
  // Проверяем, нужно ли показывать размерную линию
  // Скрываем размер только если фитинг И длина совпадают с подающим коллектором
  if (returnRightConnectionInfo) {
    let shouldShow = true;
    
    if (supplyRightConnectionInfo) {
      const fittingsMatch = returnRightConnectionInfo.fittingKey === supplyRightConnectionInfo.fittingKey;
      const lengthsMatch = Math.abs(returnRightConnectionInfo.length - supplyRightConnectionInfo.length) < 0.1;
      
      // Скрываем размер только если И фитинги И длины совпадают
      if (fittingsMatch && lengthsMatch) {
        shouldShow = false;
      }
    }
    
    if (shouldShow) {
      const dim = generateConnectionDimensionLine({
        connectionInfo: returnRightConnectionInfo,
        mainPipeId: returnPipeId,
        mainPipeDiameter: returnPipeDiameter,
        startY: returnY,
        direction: 'right',
        dimY: bottomDimY,
      });
      if (dim) dimensionLines.push(dim);
    }
  }

  return dimensionLines;
};

